import {
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SparkIcon } from '../SparkIcon';
import { DropdownItem, type DropdownItemProps } from './DropdownItem';
import { DropdownFooter } from './DropdownFooter';
import { useListNavigation } from '../../hooks/useListNavigation';
import { fuzzyFilter } from '../../utils/fuzzyFilter';
import './Dropdown.css';

/** Named group of selectable items rendered under a section subtitle. */
export type DropdownSearchSection = {
  title: string;
  items: DropdownItemProps[];
};

/** Dropdown panel with an integrated search field for filtering items. */
export type DropdownSearchProps = {
  /** Stretch to the container width instead of the compact overlay default. */
  fill?: boolean;
  /**
   * Drop overlay chrome (shadow, raised surface, inner borders) so the search
   * list sits on a parent surface such as a modal. Overlay menus stay the default.
   */
  embedded?: boolean;
  searchPlaceholder?: string;
  /** Current search query (controlled). When omitted, managed internally. */
  searchValue?: string;
  /** Fired when the search input value changes. */
  onSearchChange?: (value: string) => void;
  /** Fired when a dropdown item is selected. */
  onSelect?: (item: DropdownItemProps, index: number) => void;
  showFooter?: boolean;
  /**
   * Custom footer content rendered below the item list. When provided, replaces
   * the default Clear/Apply `DropdownFooter` (even if `showFooter` is true).
   */
  footer?: ReactNode;
  items?: DropdownItemProps[];
  /**
   * Optional titled sections. When provided, items are rendered under section
   * subtitles (empty sections are hidden). Flat `items` is ignored.
   */
  sections?: DropdownSearchSection[];
  showPrefixIcon?: boolean;
  showInfoIcon?: boolean;
  /** Message shown below the search field when no items match the query. */
  emptyText?: string;
  /** Autofocus the search input on mount (for panels that open in an overlay). */
  autoFocus?: boolean;
  /**
   * How many rows fit in the panel before the list scrolls. Defaults to 5
   * (Figma "Scroll-bar for long lists").
   */
  maxVisibleItems?: number;
  /** When false, the search field is omitted and arrow/Enter keys drive the list. */
  showSearch?: boolean;
  /**
   * Match the query fuzzily instead of as a substring (see
   * `DropdownMenu`'s `fuzzySearch`): best match first.
   */
  fuzzySearch?: boolean;
  className?: string;
};

const MAX_VISIBLE_ITEMS = 5;

export function itemSearchText(item: DropdownItemProps): string {
  if (typeof item.searchText === 'string') return item.searchText;
  const label = typeof item.label === 'string' ? item.label : '';
  if (typeof item.description === 'string' && item.description) {
    return label ? `${label} ${item.description}` : item.description;
  }
  return label;
}

function itemKey(item: DropdownItemProps, index: number): string {
  const text = itemSearchText(item);
  return text || `item-${index}`;
}

function filterByQuery(items: DropdownItemProps[], query: string, fuzzy: boolean): DropdownItemProps[] {
  if (!query) return items;
  if (fuzzy) return fuzzyFilter(items, query, itemSearchText);
  const lower = query.toLowerCase();
  return items.filter((item) => itemSearchText(item).toLowerCase().includes(lower));
}

export function DropdownSearch({
  fill = false,
  embedded = false,
  searchPlaceholder = 'Search',
  searchValue,
  onSearchChange,
  onSelect,
  showFooter = true,
  footer,
  items = [],
  sections,
  showPrefixIcon = false,
  showInfoIcon = false,
  emptyText = 'No results',
  autoFocus = false,
  maxVisibleItems = MAX_VISIBLE_ITEMS,
  showSearch = true,
  fuzzySearch = false,
  className = '',
}: DropdownSearchProps) {
  const [internalQuery, setInternalQuery] = useState('');
  const query = showSearch ? (searchValue ?? internalQuery) : '';
  const itemIdPrefix = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  const filteredSections = useMemo((): DropdownSearchSection[] => {
    if (sections) {
      return sections
        .map((section) => ({
          title: section.title,
          items: filterByQuery(section.items, query, fuzzySearch),
        }))
        .filter((section) => section.items.length > 0);
    }
    const filtered = filterByQuery(items, query, fuzzySearch);
    return filtered.length > 0 ? [{ title: '', items: filtered }] : [];
  }, [items, sections, query, fuzzySearch]);

  const flatItems = useMemo(
    () => filteredSections.flatMap((section) => section.items),
    [filteredSections],
  );

  const { activeIndex, setActiveIndex, handleKeyDown } = useListNavigation({
    itemCount: flatItems.length,
  });

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (searchValue === undefined) {
      setInternalQuery(next);
    }
    onSearchChange?.(next);
    // Typing changes the filtered list, so any keyboard highlight is stale.
    setActiveIndex(-1);
  };

  const selectItem = (item: DropdownItemProps, index: number) => {
    item.onClick?.();
    onSelect?.(item, index);
  };

  // Arrow keys move the highlight through the filtered list; Enter picks the
  // highlighted item (or the first match when nothing is highlighted yet).
  const handleListKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      const index = activeIndex >= 0 ? activeIndex : 0;
      const item = flatItems[index];
      if (item) {
        e.preventDefault();
        selectItem(item, index);
      }
      return;
    }
    handleKeyDown(e);
  };

  // Keep the keyboard-highlighted item visible inside the scrollable list.
  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`${itemIdPrefix}${activeIndex}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, itemIdPrefix]);

  useEffect(() => {
    if (!showSearch) {
      rootRef.current?.focus();
    }
  }, [showSearch]);

  const hasFooter = Boolean(footer) || showFooter;
  const rootClasses = [
    'spark-dropdown',
    'spark-dropdown-search',
    fill ? 'spark-dropdown-fill' : '',
    embedded ? 'spark-dropdown-search-embedded' : '',
    flatItems.length > maxVisibleItems ? 'spark-dropdown-scrollable' : '',
    hasFooter ? 'spark-dropdown-with-footer' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const sectionBlocks = useMemo(() => {
    let offset = 0;
    return filteredSections.map((section) => {
      const start = offset;
      offset += section.items.length;
      return { section, start };
    });
  }, [filteredSections]);

  const activeId = activeIndex >= 0 ? `${itemIdPrefix}${activeIndex}` : undefined;

  return (
    <div
      ref={rootRef}
      className={rootClasses}
      role="menu"
      tabIndex={showSearch ? undefined : 0}
      onKeyDown={showSearch ? undefined : handleListKeyDown}
      aria-activedescendant={activeId}
      style={{ '--spark-dropdown-max-visible': maxVisibleItems } as CSSProperties}
    >
      {showSearch ? (
        <div className="spark-dropdown-search-header">
          <div className="spark-dropdown-search-field">
            <input
              className="spark-dropdown-search-input"
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={handleSearchChange}
              onKeyDown={handleListKeyDown}
              autoFocus={autoFocus}
              aria-activedescendant={activeId}
            />
            <SparkIcon slug="search" size="small" className="spark-dropdown-search-icon" />
          </div>
        </div>
      ) : null}
      <div className="spark-dropdown-body">
        {flatItems.length > 0 ? (
          sectionBlocks.map(({ section, start }) => (
            <div key={section.title || 'default'} className="spark-dropdown-search-section">
              {section.title ? (
                <div className="spark-dropdown-search-section-title" role="presentation">
                  {section.title}
                </div>
              ) : null}
              {section.items.map((item, localIndex) => {
                const index = start + localIndex;
                return (
                  <DropdownItem
                    key={`${section.title}-${itemKey(item, index)}-${index}`}
                    {...item}
                    id={`${itemIdPrefix}${index}`}
                    visualState={item.visualState ?? (index === activeIndex ? 'hover' : undefined)}
                    showPrefixIcon={item.showPrefixIcon ?? showPrefixIcon}
                    showInfoIcon={item.showInfoIcon ?? showInfoIcon}
                    onClick={() => selectItem(item, index)}
                  />
                );
              })}
            </div>
          ))
        ) : (
          <div className="spark-dropdown-search-empty" role="status">
            {emptyText}
          </div>
        )}
      </div>
      {hasFooter ? (
        footer != null ? (
          <div
            className="spark-dropdown-custom-footer"
            onMouseDown={(event) => event.stopPropagation()}
          >
            {footer}
          </div>
        ) : (
          <DropdownFooter />
        )
      ) : null}
    </div>
  );
}
